import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { getOrderDetails } from '../../services/supabaseService';
import { useAuth } from '../../components/AuthProvider';
import { supabase } from '../../lib/supabase';
import { LojistaOrderDetails } from '../shopkeeper/LojistaOrderDetails';

export const OrderDetails: React.FC = () => {
  const { id } = useParams();
  const { user } = useAuth();
  const [order, setOrder] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const isLojista = user?.role_id === 6;

  // Pix payment state management
  const [pixData, setPixData] = useState<any>(() => {
    if (id) {
      const cached = localStorage.getItem(`cardumy_pix_order_${id}`);
      if (cached) {
        try { return JSON.parse(cached); } catch (e) { return null; }
      }
    }
    return null;
  });
  const [generatingPix, setGeneratingPix] = useState(false);
  const [pixError, setPixError] = useState<string | null>(null);
  const [copiedPix, setCopiedPix] = useState(false);

  useEffect(() => {
    let interval: NodeJS.Timeout | null = null;

    const fetchOrder = async () => {
      if (id) {
        const data = await getOrderDetails(id);
        setOrder(data);
        
        // Polling if order is pending payment
        const isPending = data && ['pending', 'pending_payment', 'waiting_payment'].includes(data.status);
        if (isPending) {
          interval = setInterval(async () => {
            const updatedData = await getOrderDetails(id);
            if (updatedData) {
              setOrder((prev: any) => {
                if (prev && prev.status !== updatedData.status) {
                  return updatedData;
                }
                return prev || updatedData;
              });
              if (!['pending', 'pending_payment', 'waiting_payment'].includes(updatedData.status)) {
                if (interval) clearInterval(interval);
              }
            }
          }, 5000);
        }
      }
      setLoading(false);
    };

    fetchOrder();

    // Supabase Realtime Subscription
    let channel: any = null;
    if (id) {
      channel = supabase
        .channel(`order_details_changes_${id}`)
        .on(
          'postgres_changes',
          {
            event: 'UPDATE',
            schema: 'public',
            table: 'orders',
            filter: `id=eq.${id}`,
          },
          async (payload: any) => {
            const updatedData = await getOrderDetails(id);
            if (updatedData) {
              setOrder(updatedData);
            }
          }
        )
        .subscribe();
    }

    return () => {
      if (interval) clearInterval(interval);
      if (channel) {
        supabase.removeChannel(channel);
      }
    };
  }, [id]);

  // If the logged-in user is a lojista and the order belongs to their store (or buyer is someone else), delegate to dedicated LojistaOrderDetails
  if (isLojista && order && order.buyer_id !== user?.id) {
    return <LojistaOrderDetails />;
  }

  const handleGeneratePix = async () => {
    setGeneratingPix(true);
    setPixError(null);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        throw new Error("Sua sessão expirou. Recarregue a página ou faça login novamente.");
      }

      const response = await fetch("/api/orders/pix-payment", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${session.access_token}`
        },
        body: JSON.stringify({ order_id: id })
      });

      let data: any = {};
      const contentType = response.headers.get("content-type");
      if (contentType && contentType.includes("application/json")) {
        data = await response.json();
      } else {
        const text = await response.text();
        data = { error: text || `Erro de servidor (${response.status})` };
      }

      if (!response.ok) {
        throw new Error(data.error || "Ocorreu um erro ao carregar o pagamento Pix.");
      }

      setPixData(data);
      if (id) {
        localStorage.setItem(`cardumy_pix_order_${id}`, JSON.stringify(data));
      }
    } catch (err: any) {
      console.error("[Pix Generation Catch]:", err);
      setPixError(err.message || "Erro de servidor ao processar pagamento.");
    } finally {
      setGeneratingPix(false);
    }
  };

  const payments = pixData?.transactions?.payments;
  const paymentObj = payments && payments[0];
  const qrCodeText = paymentObj?.payment_method?.qr_code || paymentObj?.qr_code || pixData?.qr_code || "";
  const qrCodeBase64 = paymentObj?.payment_method?.qr_code_base64 || paymentObj?.qr_code_base64 || pixData?.qr_code_base64 || "";

  const handleCopyPix = () => {
    if (qrCodeText) {
      navigator.clipboard.writeText(qrCodeText);
      setCopiedPix(true);
      setTimeout(() => setCopiedPix(false), 2000);
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'paid':
        return { label: 'Pago', class: 'bg-emerald-500/15 text-emerald-400 border-emerald-500/30' };
      case 'pending':
      case 'pending_payment':
      case 'waiting_payment':
        return { label: 'Aguardando Pagamento', class: 'bg-amber-500/15 text-amber-400 border-amber-500/30' };
      case 'processing':
        return { label: 'Em Preparação', class: 'bg-blue-500/15 text-blue-400 border-blue-500/30' };
      case 'shipped':
        return { label: 'Enviado', class: 'bg-indigo-500/15 text-indigo-400 border-indigo-500/30' };
      case 'delivered':
        return { label: 'Entregue', class: 'bg-teal-500/15 text-teal-400 border-teal-500/30' };
      case 'expired':
        return { label: 'Expirado', class: 'bg-slate-700/40 text-slate-400 border-slate-600/30' };
      case 'canceled':
      case 'cancelled':
        return { label: 'Cancelado', class: 'bg-rose-500/15 text-rose-400 border-rose-500/30' };
      default:
        return { label: status, class: 'bg-slate-800 text-slate-300 border-slate-700' };
    }
  };

  if (loading) {
    return (
      <div className="py-20 text-center space-y-3">
        <div className="w-8 h-8 border-3 border-purple-500/20 border-t-purple-500 rounded-full animate-spin mx-auto"></div>
        <p className="text-xs font-bold text-slate-500 uppercase tracking-wider">Carregando detalhes do pedido...</p>
      </div>
    );
  }

  if (!order) {
    return (
      <div className="py-20 text-center space-y-4">
        <p className="text-sm font-bold text-slate-400">Pedido não encontrado.</p>
        <Link to="/pedidos" className="inline-flex items-center gap-1.5 px-4 py-2 bg-purple-600 hover:bg-purple-500 text-white rounded-lg text-xs font-bold">
          <i className="fas fa-arrow-left"></i> Voltar aos Pedidos
        </Link>
      </div>
    );
  }

  const subtotal = order.products_total ?? order.order_items?.reduce((sum: number, item: any) => sum + (item.unit_price * item.quantity), 0) ?? (order.amount - (order.shipping_price || 0));
  const shippingPrice = order.shipping_price || 0;
  const totalAmount = order.amount || (subtotal + shippingPrice);
  const badge = getStatusBadge(order.status);
  const refCode = order.external_reference
    ? order.external_reference.slice(-7).toUpperCase()
    : String(order.id).slice(-7).toUpperCase();

  const isPendingPayment = ['pending', 'pending_payment', 'waiting_payment'].includes(order.status);

  return (
    <div className="max-w-4xl mx-auto space-y-6 animate-in fade-in duration-300 pb-12">
      {/* Top Header */}
      <div className="flex items-center justify-between pb-4 border-b border-slate-800">
        <div className="flex items-center gap-3">
          <Link
            to="/pedidos"
            className="w-9 h-9 bg-slate-900 border border-slate-800 rounded-lg flex items-center justify-center text-slate-400 hover:text-white hover:border-slate-700 transition-colors"
            title="Voltar aos pedidos"
          >
            <i className="fas fa-arrow-left text-xs"></i>
          </Link>
          <div>
            <div className="flex items-center gap-2">
              <h1 className="text-xl font-black text-white uppercase tracking-tight">
                Pedido #{refCode}
              </h1>
              <span className={`text-[9px] font-black uppercase px-2 py-0.5 rounded-full border ${badge.class}`}>
                {badge.label}
              </span>
            </div>
            <p className="text-[11px] text-slate-500 font-medium">
              Realizado em {new Date(order.created_at).toLocaleDateString('pt-BR', {
                day: '2-digit',
                month: '2-digit',
                year: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
              })}
            </p>
          </div>
        </div>

        {order.store?.name && (
          <div className="hidden sm:flex items-center gap-2 text-xs font-bold text-slate-400 bg-slate-900 px-3 py-1.5 rounded-lg border border-slate-800">
            <i className="fas fa-store text-purple-400"></i>
            <span>{order.store.name}</span>
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {/* Left Column (2 cols) */}
        <div className="md:col-span-2 space-y-5">
          {/* Pix / Payment Section if Pending */}
          {isPendingPayment && (
            (order.payment_method?.slug === 'cash' || order.payment_method?.requires_online === false) ? (
              <div className="bg-amber-950/20 border border-amber-500/30 rounded-xl p-4 space-y-2">
                <div className="flex items-center gap-2 text-amber-400 text-xs font-black uppercase tracking-wider">
                  <i className="fas fa-money-bill-1-wave"></i>
                  <span>Pagamento em Dinheiro (Balcão)</span>
                </div>
                <p className="text-xs text-slate-300 leading-relaxed">
                  Combine diretamente com o lojista para realizar o pagamento no balcão da loja. Assim que recebido, o status será alterado para <strong>Pago</strong>.
                </p>
              </div>
            ) : (
              <div className="bg-purple-950/20 border border-purple-500/30 rounded-xl p-4 sm:p-5 space-y-4">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                  <div>
                    <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-md bg-emerald-500/15 border border-emerald-500/30 text-emerald-400 text-[10px] font-black uppercase tracking-wider mb-1.5">
                      <i className="fa-brands fa-pix text-[10px]"></i> Pix Instantâneo
                    </span>
                    <h3 className="text-sm font-black text-white uppercase tracking-tight">
                      Finalize seu pagamento via Pix
                    </h3>
                    <p className="text-xs text-slate-400 mt-0.5">
                      Pagamento seguro processado na hora pelo Mercado Pago.
                    </p>
                  </div>

                  {!pixData && (
                    <button
                      onClick={handleGeneratePix}
                      disabled={generatingPix}
                      className="bg-purple-600 hover:bg-purple-500 text-white font-bold text-xs px-4 py-2 rounded-lg transition-colors flex items-center justify-center gap-1.5 cursor-pointer shadow-md shadow-purple-950/40 disabled:opacity-50 shrink-0"
                    >
                      {generatingPix ? (
                        <>
                          <i className="fas fa-spinner fa-spin text-xs"></i>
                          <span>Gerando Pix...</span>
                        </>
                      ) : (
                        <>
                          <i className="fa-brands fa-pix text-sm"></i>
                          <span>Pagar com Pix</span>
                        </>
                      )}
                    </button>
                  )}
                </div>

                {pixError && (
                  <div className="p-3 rounded-lg bg-rose-500/15 border border-rose-500/30 text-rose-300 text-xs flex items-center gap-2">
                    <i className="fas fa-triangle-exclamation"></i>
                    <span>{pixError}</span>
                  </div>
                )}

                {pixData && (
                  <div className="pt-3 border-t border-purple-500/20 flex flex-col sm:flex-row items-center gap-5">
                    <div className="w-36 h-36 bg-white p-2 rounded-xl shadow-lg flex items-center justify-center shrink-0 border-2 border-purple-500/40">
                      {qrCodeBase64 ? (
                        <img
                          src={`data:image/png;base64,${qrCodeBase64}`}
                          alt="QR Code Pix"
                          className="w-full h-full object-contain"
                        />
                      ) : qrCodeText ? (
                        <img
                          src={`https://api.qrserver.com/v1/create-qr-code/?size=160x160&data=${encodeURIComponent(qrCodeText)}`}
                          alt="QR Code Pix"
                          className="w-full h-full object-contain"
                        />
                      ) : (
                        <span className="text-slate-400 text-xs">QR Code</span>
                      )}
                    </div>

                    <div className="flex-1 w-full space-y-2">
                      <span className="text-[10px] font-black uppercase text-slate-400 tracking-wider block">
                        Código Pix Copia e Cola
                      </span>
                      <div className="flex items-center gap-1.5 bg-slate-900 border border-slate-800 rounded-lg p-1.5 pl-3">
                        <input
                          type="text"
                          readOnly
                          value={qrCodeText || 'Nenhum código disponível'}
                          className="bg-transparent text-slate-300 font-mono text-xs w-full focus:outline-none select-all truncate"
                        />
                        <button
                          onClick={handleCopyPix}
                          className="bg-purple-600 hover:bg-purple-500 text-white px-3 py-1.5 rounded-md text-xs font-bold transition-colors cursor-pointer shrink-0"
                        >
                          {copiedPix ? (
                            <>
                              <i className="fas fa-check text-emerald-400 mr-1"></i> Copiado
                            </>
                          ) : (
                            <>
                              <i className="fas fa-copy mr-1"></i> Copiar
                            </>
                          )}
                        </button>
                      </div>
                      <p className="text-[11px] text-slate-400">
                        Após pagar no aplicativo do seu banco, o sistema reconhecerá o pagamento automaticamente em alguns segundos.
                      </p>
                    </div>
                  </div>
                )}
              </div>
            )
          )}

          {/* Items Card */}
          <div className="bg-slate-900/60 border border-slate-800 rounded-xl overflow-hidden">
            <div className="px-5 py-4 border-b border-slate-800 flex items-center justify-between">
              <h3 className="text-xs font-black uppercase tracking-wider text-white flex items-center gap-2">
                <i className="fas fa-shopping-bag text-purple-400"></i>
                Itens do Pedido ({order.order_items?.length || 0})
              </h3>
            </div>
            <div className="divide-y divide-slate-800/60 p-2">
              {order.order_items?.map((item: any) => (
                <div key={item.id} className="flex items-center gap-3.5 p-3 hover:bg-slate-800/30 rounded-lg transition-colors">
                  <div className="w-12 h-12 bg-slate-800 rounded-lg overflow-hidden shrink-0 border border-slate-700/60">
                    <img
                      src={item.product?.image_url || 'https://via.placeholder.com/150'}
                      alt={item.product?.name || 'Produto'}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h4 className="text-xs font-bold text-white truncate">
                      {item.product?.name || 'Produto'}
                    </h4>
                    <p className="text-[11px] text-slate-500 mt-0.5">
                      Qtd: <span className="text-slate-300 font-bold">{item.quantity}</span> × R$ {(item.unit_price || 0).toFixed(2)}
                    </p>
                  </div>
                  <div className="text-right shrink-0">
                    <span className="text-xs font-black text-white">
                      R$ {((item.unit_price || 0) * (item.quantity || 1)).toFixed(2)}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Right Column (1 col): Status & Financial Summary */}
        <div className="space-y-5">
          {/* Status Details */}
          <div className="bg-slate-900/60 border border-slate-800 rounded-xl p-5 space-y-4">
            <h3 className="text-xs font-black uppercase tracking-wider text-white flex items-center gap-2">
              <i className="fas fa-circle-info text-purple-400"></i>
              Status do Pedido
            </h3>

            <div>
              <span className="text-[10px] font-black uppercase tracking-wider text-slate-500 block mb-1">
                Estado Atual
              </span>
              <span className={`text-[10px] font-black uppercase px-2.5 py-1 rounded-full border ${badge.class}`}>
                {badge.label}
              </span>
            </div>

            {order.store?.name && (
              <div className="pt-3 border-t border-slate-800">
                <span className="text-[10px] font-black uppercase tracking-wider text-slate-500 block mb-1">
                  Loja Vendedora
                </span>
                <p className="text-xs font-bold text-white flex items-center gap-1.5">
                  <i className="fas fa-store text-purple-400"></i>
                  {order.store.name}
                </p>
              </div>
            )}

            {order.payment_method?.name && (
              <div className="pt-3 border-t border-slate-800">
                <span className="text-[10px] font-black uppercase tracking-wider text-slate-500 block mb-1">
                  Forma de Pagamento
                </span>
                <p className="text-xs font-bold text-white flex items-center gap-1.5">
                  <i className="fas fa-credit-card text-purple-400"></i>
                  {order.payment_method.name}
                </p>
              </div>
            )}
          </div>

          {/* Financial Summary */}
          <div className="bg-slate-900/60 border border-purple-500/20 bg-purple-950/10 rounded-xl p-5 space-y-3">
            <h3 className="text-xs font-black uppercase tracking-wider text-white">
              Resumo Financeiro
            </h3>

            <div className="space-y-2 text-xs">
              <div className="flex justify-between text-slate-400">
                <span>Subtotal</span>
                <span className="font-bold text-slate-200">R$ {subtotal.toFixed(2)}</span>
              </div>
              <div className="flex justify-between text-slate-400">
                <span>Frete</span>
                <span className="font-bold text-slate-200">
                  {shippingPrice > 0 ? `R$ ${shippingPrice.toFixed(2)}` : 'Grátis / Retirada'}
                </span>
              </div>
              <div className="pt-2 border-t border-slate-800 flex justify-between items-baseline">
                <span className="text-xs font-black uppercase tracking-wider text-white">Total</span>
                <span className="text-lg font-black text-purple-400">
                  R$ {totalAmount.toFixed(2)}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
